import { config } from '../../config/Config.js';

/**
 * The app's connection / session / version state, in one place outside React, so the axios layer, Firebase and the
 * version poll can all report into it and one status bar and one dialog read it. Every state here has a way out the
 * user can see (see docs in AppStatusBar / SessionExpiredDialog) - none of them is a dead end.
 *
 *   offline            the browser says there is no network
 *   serverUnreachable  requests reach no server (network error / 502); a probe clears it when the server answers
 *   serverRetryPaused  the outage has lasted long enough that probing stopped; the user's next move restarts it
 *   sessionExpired     the session can no longer be refreshed - the sign-in dialog is up (sessionExpiry.js)
 *   newVersion         a different build is deployed than the one this tab runs (versionCheck.js)
 */

/** Fired when the app is back in touch with the server (online again, the server answers). Loaders that gave up re-run. */
export const RECONNECTED_EVENT = 'gp-app-reconnected';

// Probing starts only after a request reached no server. It backs off to once a minute, and every wait is jittered so
// tabs that lost the server together do not probe it together. Nothing probes while the browser is offline, from a
// hidden tab, or past GIVE_UP_AFTER_MS - each of those waits for the browser, the tab or the user to come back.
export const PROBE_DELAYS_MS = [5000, 10000, 20000, 40000, 60000];
export const GIVE_UP_AFTER_MS = 10 * 60 * 1000;
const PROBE_TIMEOUT_MS = 5000;
// The server's Retry-After is waited out, up to this.
export const MAX_RETRY_AFTER_MS = 15 * 60 * 1000;
// When the server is back, each tab tells its loaders at a random moment within this, so a just-restarted server is not
// hit by every open tab reloading in the same second.
export const RECONNECT_SPREAD_MS = 3000;
// Tabs of this app tell each other when one probes and when the server is back, so several tabs probe like one.
export const STATUS_CHANNEL = 'gp-app-status';

const jittered = (ms) => Math.round(ms * (0.75 + Math.random() * 0.5));
const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

let state = {
    offline: isOffline(),
    serverUnreachable: false,
    serverRetryPaused: false,
    sessionExpired: false,
    newVersion: null,
};
const listeners = new Set();

export const getAppStatus = () => state;

export const subscribeAppStatus = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const setAppStatus = (patch) => {
    const next = { ...state, ...patch };
    if (Object.keys(next).every((k) => next[k] === state[k])) return;
    state = next;
    listeners.forEach((l) => l(state));
};

let announceTimer = null;

const announceReconnected = () => {
    clearTimeout(announceTimer);
    announceTimer = setTimeout(() => {
        try {
            window.dispatchEvent(new Event(RECONNECTED_EVENT));
        } catch (_) { /* non-browser - ignore */ }
    }, Math.random() * RECONNECT_SPREAD_MS);
};

let channel = null;
try {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(STATUS_CHANNEL);
    channel?.unref?.();
} catch {
    channel = null;
}
const tellOtherTabs = (message) => {
    try {
        channel?.postMessage(message);
    } catch { /* closed or unsupported - each tab then probes for itself */ }
};

/** Retry-After as milliseconds to wait (seconds or an HTTP date), capped at MAX_RETRY_AFTER_MS; 0 when absent. */
export const parseRetryAfter = (value, nowMs = Date.now()) => {
    if (value === undefined || value === null || value === '') return 0;
    const seconds = Number(value);
    const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - nowMs;
    if (!Number.isFinite(ms) || ms <= 0) return 0;
    return Math.min(ms, MAX_RETRY_AFTER_MS);
};

let probeTimer = null;
let probeAttempt = 0;
let outageStartedAt = 0;
let retryAfterUntil = 0;
// What the next probe waits for instead of a timer: 'online', 'visible' or 'activity' (after GIVE_UP_AFTER_MS).
let waitingFor = null;

const noteRetryAfter = (ms) => {
    if (ms > 0) retryAfterUntil = Math.max(retryAfterUntil, Date.now() + ms);
};

/** One HEAD to the API server: whether it is there, and any Retry-After it sent. */
const probe = async () => {
    if (!config.serverBaseUrl) return { reachable: true, retryAfterMs: 0 };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
        const sameOrigin = new URL(config.serverBaseUrl, window.location.href).origin === window.location.origin;
        const res = await fetch(`${config.serverBaseUrl}/`, {
            method: 'HEAD', cache: 'no-store', mode: sameOrigin ? 'same-origin' : 'no-cors', signal: controller.signal,
        });
        // Another origin answers opaquely (no CORS on a bare HEAD), so getting an answer at all has to do.
        if (res.type === 'opaque' || res.status < 500) return { reachable: true, retryAfterMs: 0 };
        return { reachable: false, retryAfterMs: parseRetryAfter(res.headers?.get?.('Retry-After')) };
    } catch {
        return { reachable: false, retryAfterMs: 0 };
    } finally {
        clearTimeout(timer);
    }
};

/** Whether the API server answers: any answer below 500. */
export const probeServer = async () => (await probe()).reachable;

const scheduleProbe = ({ advance = true } = {}) => {
    clearTimeout(probeTimer);
    if (!state.serverUnreachable) return;
    const backoff = jittered(PROBE_DELAYS_MS[Math.min(probeAttempt, PROBE_DELAYS_MS.length - 1)]);
    if (advance) probeAttempt += 1;
    // eslint-disable-next-line no-use-before-define
    probeTimer = setTimeout(runProbe, Math.max(backoff, retryAfterUntil - Date.now()));
};

const probeAndSettle = async () => {
    tellOtherTabs({ type: 'probing' });
    const answer = await probe();
    if (!state.serverUnreachable) return;
    // eslint-disable-next-line no-use-before-define
    if (answer.reachable) markServerReachable();
    else {
        noteRetryAfter(answer.retryAfterMs);
        scheduleProbe();
    }
};

const runProbe = () => {
    if (!state.serverUnreachable) return;
    if (isOffline()) {
        waitingFor = 'online';
        return;
    }
    if (isHidden()) {
        waitingFor = 'visible';
        return;
    }
    if (Date.now() - outageStartedAt >= GIVE_UP_AFTER_MS) {
        waitingFor = 'activity';
        setAppStatus({ serverRetryPaused: true });
        return;
    }
    probeAndSettle();
};

const startNewWindow = () => {
    outageStartedAt = Date.now();
    probeAttempt = 0;
    setAppStatus({ serverRetryPaused: false });
};

/** The browser, the tab or the user is back: run the probe that was waiting for it. */
const resume = (cause) => {
    if (!state.serverUnreachable || !waitingFor) return;
    if (waitingFor === 'online' && cause !== 'online') return;
    if (waitingFor === 'visible' && cause !== 'visible') return;
    // Activity is any click, key or return to the tab; coming back online is not the user doing anything.
    if (waitingFor === 'activity' && cause === 'online') return;
    // Something real changed - the user is back, the tab is shown, the network returned - so time spent waiting for it
    // does not count against the ten minutes: a fresh window of probing.
    if (waitingFor === 'activity' || Date.now() - outageStartedAt >= GIVE_UP_AFTER_MS) {
        startNewWindow();
    }
    waitingFor = null;
    if (retryAfterUntil > Date.now()) scheduleProbe({ advance: false });
    else runProbe();
};

/** A request reached no server. Shows "Can't reach GeoPlay" and probes, backing off, until it answers. */
export const markServerUnreachable = ({ retryAfterMs = 0 } = {}) => {
    noteRetryAfter(retryAfterMs);
    if (state.serverUnreachable) {
        if (retryAfterMs > 0 && !waitingFor) scheduleProbe({ advance: false });
        return;
    }
    waitingFor = null;
    outageStartedAt = Date.now();
    probeAttempt = 0;
    setAppStatus({ serverUnreachable: true, serverRetryPaused: false });
    scheduleProbe();
};

/** The server answered again (a probe, any request, or another tab): clear the bar and let loaders that gave up retry. */
export const markServerReachable = ({ fromOtherTab = false } = {}) => {
    clearTimeout(probeTimer);
    waitingFor = null;
    retryAfterUntil = 0;
    if (!state.serverUnreachable) return;
    setAppStatus({ serverUnreachable: false, serverRetryPaused: false });
    if (!fromOtherTab) tellOtherTabs({ type: 'reachable' });
    announceReconnected();
};

/** "Retry Now" on the bar: probe at once, whatever the backoff or Retry-After says. Offline, there is nothing to ask. */
export const retryServerNow = async () => {
    if (isOffline()) {
        setAppStatus({ offline: true });
        return;
    }
    clearTimeout(probeTimer);
    if (waitingFor === 'activity' || state.serverRetryPaused) startNewWindow();
    waitingFor = null;
    if (!state.serverUnreachable) return;
    await probeAndSettle();
};

/** What another tab said: the server is back, or it is probing now so this tab can skip its turn. */
export const handleOtherTabMessage = (message) => {
    if (!state.serverUnreachable) return;
    if (message?.type === 'reachable') markServerReachable({ fromOtherTab: true });
    else if (message?.type === 'probing' && !waitingFor) scheduleProbe({ advance: false });
};

if (typeof window !== 'undefined') {
    if (channel) channel.onmessage = (event) => handleOtherTabMessage(event.data);
    window.addEventListener('offline', () => setAppStatus({ offline: true }));
    window.addEventListener('online', () => {
        setAppStatus({ offline: false });
        announceReconnected();
        resume('online');
    });
    document.addEventListener('visibilitychange', () => { if (!isHidden()) resume('visible'); });
    const onActivity = () => { if (waitingFor === 'activity') resume('activity'); };
    window.addEventListener('pointerdown', onActivity, { capture: true, passive: true });
    window.addEventListener('keydown', onActivity, { capture: true, passive: true });
}
